import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from '../lib/api.js';

export type AdminPrivateCourse = {
  id: string;
  providerName: string;
  name: string;
  cricosCode: string | null;
  category: 'vocational' | 'higher_education' | 'research' | 'trade';
  subCategory: string;
  aqfLevel: string | null;
  durationText: string | null;
  durationMonths: number | null;
  campusLocations: string[];
  intakeMonths: number[];
  annualTuitionCents: number | null;
  totalProgramCents: number | null;
  oshcAnnualCents: number | null;
  applicationFeeCents: number | null;
  materialFeesCents: number | null;
  ieltsOverall: string | null;
  ieltsMinBand: string | null;
  ieltsRequirementText: string | null;
  pteOverall: number | null;
  pteMinSkill: number | null;
  pteRequirementText: string | null;
  toeflOverall: number | null;
  toeflRequirementText: string | null;
  otherEnglishText: string | null;
  academicPrerequisites: string | null;
  courseStructure: string | null;
  careerOutcomesSummary: string | null;
  pathways: string | null;
  anzscoCodes: string[];
  visaSubclassRelevance: string[];
  documentsRequired: string[];
  scholarshipsAvailable: boolean | null;
  scholarshipsText: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreatePrivateCoursePayload = Partial<
  Omit<AdminPrivateCourse, 'id' | 'createdAt' | 'updatedAt'>
> & {
  providerName: string;
  name: string;
  category: AdminPrivateCourse['category'];
  subCategory: string;
};

export function usePrivateCourses(filters: { category?: string; q?: string }) {
  return useQuery({
    queryKey: ['admin', 'private-courses', filters.category ?? 'all', filters.q ?? ''],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.category) params.set('category', filters.category);
      if (filters.q) params.set('q', filters.q);
      const qs = params.toString();
      return apiGet<{ items: AdminPrivateCourse[] }>(
        `/admin/private-courses${qs ? `?${qs}` : ''}`,
      );
    },
    staleTime: 30_000,
  });
}

export function usePrivateCourse(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'private-courses', 'detail', id],
    queryFn: () => apiGet<{ course: AdminPrivateCourse }>(`/admin/private-courses/${id}`),
    enabled: !!id,
  });
}

export function useCreatePrivateCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePrivateCoursePayload) =>
      apiPost<{ course: AdminPrivateCourse }>(`/admin/private-courses`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'private-courses'] });
      qc.invalidateQueries({ queryKey: ['courses'] }); // consultant catalogue too
    },
  });
}

export function useUpdatePrivateCourse(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CreatePrivateCoursePayload>) =>
      apiPatch<{ course: AdminPrivateCourse }>(`/admin/private-courses/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'private-courses'] });
      qc.invalidateQueries({ queryKey: ['courses'] });
    },
  });
}

export function useDeletePrivateCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<void>(`/admin/private-courses/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'private-courses'] });
      qc.invalidateQueries({ queryKey: ['courses'] });
    },
  });
}
